<template>
  <v-container fluid>
    <v-card>
      <v-toolbar :dark="$vuetify.theme.dark" color="primary">
        <v-col cols="10" class="text-left">
          <v-toolbar-title class="textwithbackground--text">
            {{ $t('Список редактируемых страниц') }}
          </v-toolbar-title>
        </v-col>
        <v-col cols="2" class="text-right">
          <btn-icon-tip
              v-if="isMainAdmin"
              icon="add"
              icon-color="white"
              :disabled="isLoading"
              :tip="$t('Добавить страницу')"
              @click="openAddDialog" />
        </v-col>
      </v-toolbar>
      <general-error v-if="!!generalError" />
      <v-row class="px-2">
        <v-col>
          <v-data-table
              :headers="headers"
              :items="items"
              :items-per-page="itemsPerPage"
              disable-filtering
              item-key="tag"
              :loading="isLoading"
              :locale="systemLocale">
            <template v-slot:progress>
              <v-progress-linear :active="isLoading" color="loader" indeterminate />
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.tag="{ item }">
              {{ item.tag }}
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.name="{ item }">
              {{ item.name }}
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.description="{ item }">
              {{ item.description }}
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.editDate="{ item }">
              {{ item.editDate
                ? $moment(item.editDate).format('DD.MM.YYYY HH:mm')
                : null }}
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.publishDate="{ item }">
              {{ item.publishDate
                ? $moment(item.publishDate).format('DD.MM.YYYY HH:mm')
                : null }}
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.changesPublished="{ item }">
              <icon-tip
                  :icon="item.changesPublished
                    ? 'public'
                    : 'public_off'"
                  :icon-color="item.changesPublished
                    ? 'success'
                    : 'warning'"
                  :tip="$t('Текущий статус: {0}', [ item.changesPublished
                    ? $t('Все изменения опубликованы')
                    : $t('Есть неопубликованные изменения') ] )" />
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.actions="{ item }">
              <btn-icon-tip
                  v-if="isAdmin"
                  icon="settings"
                  :tip="$t('Редактировать описание')"
                  :disabled="isLoading"
                  @click="openEditDialog(item.tag)" />
              <route-btn-tip
                  :id="item.tag"
                  icon="edit"
                  :tip="$t('Редактировать содержимое')"
                  :disabled="isLoading"
                  :to="{ path: `${urlRootPath}/staticPage/${item.tag}` }" />
              <btn-icon-tip
                  v-if="isMainAdmin"
                  icon="delete"
                  icon-color="warning"
                  :tip="$t('Удалить')"
                  :disabled="isLoading"
                  @click="openConfirmDialog(item.tag)" />
              <btn-icon-tip
                  v-if="!item.changesPublished &&
                    !!item.editedContent"
                  icon="publish"
                  :tip="$t('Опубликовать')"
                  :disabled="isLoading"
                  @click="openPublicationConfirmDialog(item.tag)" />
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-card>
    <confirm-dialog
        v-if="confirmDialog.show"
        :dialog-prop="confirmDialog"
        @close="closeDialog('confirmDialog')" />
    <edit-page-dialog
        v-if="editPageDialog.show"
        :dialog-prop="editPageDialog"
        @close="closeDialog('editPageDialog')" />
  </v-container>
</template>

<script>

  import BtnIconTip from '@/components/reused/ui/BtnIconTip.vue'
  import IconTip from '@/components/reused/ui/IconTip.vue'
  import RouteBtnTip from '@/components/reused/ui/RouteBtnTip.vue'
  import EditPageDialog from './EditPageDialog.vue'
  import ConfirmDialog from '@/components/reused/common/ConfirmDialog.vue'
  import GeneralError from '@/components/reused/common/GeneralError.vue'
  import { mapActions, mapState } from 'vuex'
  import { STATIC_PAGES } from '@/constants/store.js'

  export default {
    components: {
      BtnIconTip,
      RouteBtnTip,
      IconTip,
      GeneralError,
      EditPageDialog,
      ConfirmDialog
    },
    data () {
      return {
        isLoading: false,
        itemsPerPage: -1,
        editPageDialog: {
          id: null,
          callback: null,
          show: null
        },
        confirmDialog: {
          show: false,
          additional: null,
          text: null,
          callback: null,
          warning: true
        }
      }
    },
    computed: {
      ...mapState(STATIC_PAGES.NAMESPACE, {
        items: state => (state[STATIC_PAGES.STATE.PAGES] || {})
      }),
      headers () {
        return [
          { text: this.$t('Идентификатор страницы'), align: 'start', sortable: true, value: 'tag' },
          { text: this.$t('Наименование'), align: 'start', sortable: true, value: 'name' },
          { text: this.$t('Описание'), align: 'start', sortable: false, value: 'description' },
          { text: this.$t('Статус публикации'), align: 'center', sortable: false, value: 'changesPublished' },
          { text: this.$t('Изменено'), align: 'start', sortable: false, value: 'editDate' },
          { text: this.$t('Опубликовано'), align: 'start', sortable: false, value: 'publishDate' },
          { text: this.$t('Действия'), align: 'start', sortable: false, value: 'actions' }
        ]
      }
    },
    created () {
      const vm = this
      vm.clearStore().then(() => {
        vm.getAllStaticPages()
      })
    },
    beforeDestroy () {
      this.clearStore()
    },
    methods: {
      ...mapActions(STATIC_PAGES.NAMESPACE, {
        getAllStaticPagesAction: STATIC_PAGES.ACTIONS.GET_ALL_STATIC_PAGES,
        publishPageContentAction: STATIC_PAGES.ACTIONS.PUBLISH_PAGE_CONTENT,
        deletePageAction: STATIC_PAGES.ACTIONS.DELETE_PAGE,
        clearAllStaticPagesAction: STATIC_PAGES.ACTIONS.CLEAR_ALL_STATIC_PAGES
      }),
      clearStore: async function () {
        await Promise.all(
          [
            this.clearAllStaticPagesAction()
          ])
      },
      openConfirmDialog: function (id) {
        this.confirmDialog.additional = id
        this.confirmDialog.text = this.$t('Страница будет удалена. Продолжить?')
        this.confirmDialog.callback = this.deletePage
        this.confirmDialog.warning = true
        this.confirmDialog.show = true
      },
      openPublicationConfirmDialog: function (id) {
        this.confirmDialog.additional = id
        this.confirmDialog.text = this.$t('Страница будет опубликована. Продолжить?')
        this.confirmDialog.callback = this.publishPageContent
        this.confirmDialog.warning = true
        this.confirmDialog.show = true
      },
      openAddDialog: function () {
        this.editPageDialog.callback = this.getAllStaticPages
        this.editPageDialog.show = true
      },
      openEditDialog: function (id) {
        this.editPageDialog.id = id
        this.editPageDialog.callback = this.getAllStaticPages
        this.editPageDialog.show = true
      },
      getAllStaticPages: async function () {
        try {
          this.isLoading = true
          await this.getAllStaticPagesAction()
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      publishPageContent: async function (tag) {
        try {
          this.isLoading = true
          await this.publishPageContentAction({
            tag
          })
          await this.getAllStaticPages()

          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      deletePage: async function (tag) {
        try {
          this.isLoading = true
          await this.deletePageAction({
            tag
          })
          await this.getAllStaticPages()
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      }
    }
  }
</script>
